import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { collection, doc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Match, TournamentCode } from '../types';
import { useDivision } from './DivisionContext';
import { getFirebasePrefix } from '../utils';

// Define the shape of the data the context will provide
interface MatchesContextType {
    matches: Match[],
    loading: boolean,
    tournamentCodes: TournamentCode[],
    updateMatch: (updatedMatch: Match) => Promise<void>
}

// Create the context with a default value
const MatchesContext = createContext<MatchesContextType | undefined>(undefined);

// Create the Provider component
export const MatchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [hash, setHash] = useState(window.location.hash);
    useEffect(() => {
        const handleHashChange = () => setHash(window.location.hash);
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const [matches, setMatches] = useState<Match[]>([]);
    const [tournamentCodes, setTournamentCodes] = useState<TournamentCode[]>([]);
    const [loading, setLoading] = useState(true);

    const { division } = useDivision();

    // This effect runs once when the provider mounts to fetch all player data
    useEffect(() => {
        const fetchMatches = async () => {
            setLoading(true);
            try {
                const prefix = getFirebasePrefix();
                const matchesRef = doc(db, 'matches', `${prefix}_${division}`)
                const snapshot = await getDoc(matchesRef);

                if (snapshot.exists()) {
                    setMatches(snapshot.data().matches);
                } else {
                    setMatches([]);
                }
            } catch (error) {
                console.error("Failed to fetch player data:", error);
            } finally {
               setLoading(false);
            }
        };

        const fetchTournamentCodes = async () => {
            setLoading(true);
            try {
                const collectionName = "matches";
                const matchesRef = collection(db, collectionName);

                const q = query(matchesRef, where("division", "==", division));
                
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    console.warn(`No matches found for division ${division} in collection ${collectionName}`);
                    setTournamentCodes([]);
                }
                
                setTournamentCodes(querySnapshot.docs.map((d) => ({code: d.id, matchId: d.data().matchId, status: d.data().status, winnerId: d.data().winnerId})));
            } catch (error) {
                console.error(`Error querying for match for division ${division} data:`, error);
                setTournamentCodes([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMatches();
        fetchTournamentCodes();
    }, [division, hash]);


  const updateMatch = async (updatedMatch: Match) => {
    try {
      const prefix = getFirebasePrefix();
      const matchExists = matches.some(m => m.id === updatedMatch.id || String(m.id) === String(updatedMatch.id));
      const updatedList = matchExists
        ? matches.map(m => (m.id === updatedMatch.id || String(m.id) === String(updatedMatch.id)) ? updatedMatch : m)
        : [...matches, updatedMatch];
      await updateDoc(doc(db, 'matches', `${prefix}_${division}`), { matches: updatedList });
      setMatches(updatedList);
    } catch (error) {
      console.error("Failed to update match:", error);
      throw error;
    }
  };

  const value = {
    matches,
    loading,
    tournamentCodes,
    updateMatch,
  };

  return <MatchesContext.Provider value={value}>{children}</MatchesContext.Provider>;
};

// Create a custom hook for easy access to the context
export const useGameMatches = (): MatchesContextType => {
  const context = useContext(MatchesContext);
  if (context === undefined) {
    throw new Error('useGameMatches must be used within a MatchesProvider');
  }
  return context;
};
